import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from '../../src/Context/AuthContext';
import Modal from '../../Components/Shared/Modal';
import AuthHeader from '../../Components/Account/AuthHeader';


const RegisterPage = ({ portalSettings }) => {
    //State Variables
    const { login, isAuthenticated } = useAuth();
    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        ssnLast4: "",
        birthDate: "",
        username: "",
        email: "",
        confirmEmail: "",
        password: "",
        confirmPassword: "",
    });

    const [modalData, setModalData] = useState({
        show: false,
        message: "",
        onAction: null,
    });

    const navigate = useNavigate();

    //ENV Variables
    const companyName = import.meta.env.VITE_COMPANY_NAME;
    const apiURL = import.meta.env.VITE_API;

    //Set Site Title
    document.title = `${companyName} - Register User`;

    // If loading auth or no portal settings yet, just render nothing
    if (!portalSettings) return null;

    const psm = portalSettings || {};

    useEffect(() => {
        if (isAuthenticated) {
            navigate('/home');
        }
        if (psm.hasPolicyPortal == false) {
            if (psm.hasAgentPortal == true) {
                navigate('/registerAgent');
            }
            else {
                navigate('/login');
            }
        }
    }, [isAuthenticated, navigate]);

    const handleChange = (e) =>
        setFormData({ ...formData, [e.target.name]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Basic validation
        if (formData.email !== formData.confirmEmail) {
            setModalData({
                show: true,
                message: "Emails do not match",
                onAction: null,
            });
            return;
        }
        if (formData.password !== formData.confirmPassword) {
            setModalData({
                show: true,
                message: "Passwords do not match",
                onAction: null,
            });
            return;
        }

        const payload = {
            polNo: formData.polNo,
            firstName: formData.firstName,
            lastName: formData.lastName,
            lastFour: formData.ssnLast4,
            birthDate: formData.birthDate,
            userName: formData.username,
            email: formData.email,
            password: formData.password,
        };

        try {
            const res = await fetch(`${apiURL}/account/register-user`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            const data = await res.json();

            if (res.ok) {
                setModalData({
                    show: true,
                    message: data.message || "Registration successful!",
                    onAction: () => navigate("/login"),
                });
            } else {
                setModalData({
                    show: true,
                    message: data.message || "Registration failed",
                    onAction: null,
                });
            }
        } catch (err) {
            console.error("Registration error:", err);
            setModalData({
                show: true,
                message: "Something went wrong. Please try again.",
                onAction: null,
            });
        }
    };

    return (
        <div className="auth-page">
            <div className="auth-container">
                <AuthHeader portalSettings={portalSettings} />
                <h2 className="baseHeader">Register User</h2>
                <form className="baseForm" onSubmit={handleSubmit}>

                    <label className="baseLabel">Policy</label>
                    <input
                        type="text"
                        name="polNo"
                        className="baseInput"
                        value={formData.polNo}
                        onChange={handleChange}
                        maxLength="10"
                        pattern="[A-Za-z0-9]{10}"
                        required
                    />

                    <label className="baseLabel">First Name</label>
                    <input
                        type="text"
                        name="firstName"
                        className="baseInput"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                    />

                    <label className="baseLabel">Last Name</label>
                    <input
                        type="text"
                        name="lastName"
                        className="baseInput"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                    />

                    <label className="baseLabel">Username</label>
                    <input
                        type="text"
                        name="username"
                        className="baseInput"
                        value={formData.username}
                        onChange={handleChange}
                        required
                    />

                    <label className="baseLabel">Email</label>
                    <input
                        type="email"
                        name="email"
                        className="baseInput"
                        value={formData.email}
                        onChange={handleChange}
                        required
                    />

                    <label className="baseLabel">Confirm Email</label>
                    <input
                        type="email"
                        name="confirmEmail"
                        className="baseInput"
                        value={formData.confirmEmail}
                        onChange={handleChange}
                        required
                    />

                    <label className="baseLabel">Last 4 SSN</label>
                    <input
                        type="text"
                        name="ssnLast4"
                        className="baseInput"
                        value={formData.ssnLast4}
                        onChange={handleChange}
                        required
                        maxLength="4"
                        pattern="\d{4}"
                    />

                    <label className="baseLabel">{psm.birthDateRegisterType} Date of Birth</label>
                    <input
                        type="date"
                        name="birthDate"
                        className="baseInput"
                        value={formData.birthDate}
                        onChange={handleChange}
                        required
                    />

                    <label className="baseLabel">Password</label>
                    <input
                        type="password"
                        name="password"
                        className="baseInput"
                        value={formData.password}
                        onChange={handleChange}
                        required
                    />

                    <label className="baseLabel">Confirm Password</label>
                    <input
                        type="password"
                        name="confirmPassword"
                        className="baseInput"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        required
                    />

                    <button className="baseButton" type="submit">Register</button>
                </form>

                <div className="auth-links">
                    <Link to="/login">Back to Login</Link>

                    {psm.hasAgentPortal && (
                        <Link to="/registerAgent">Register Agent</Link>
                    )}

                    {psm.hasPolicyPortal && (
                        <Link to="/registerEntity">Register Entity</Link>
                    )}
                </div>

            </div>

            <Modal
                show={modalData.show}
                message={modalData.message}
                onClose={() => setModalData({ ...modalData, show: false })}
                onAction={modalData.onAction}
                actionLabel="Go to Login"
            />
        </div>
    );
};

export default RegisterPage;
